import { UrlMatchResult, UrlSegment } from '@angular/router';
import { BriefProgressEnum } from '../../common/general-types/briefs.dto';

export const DEFAULT_MANAGE_BRIEF_TAB = BriefProgressEnum.ActivityFeed;

/**
 * One route for `/manage-brief/:briefId`, `/:tab`, and `/:tab/:subTab` so Angular
 * reuses ManageBriefTabsComponent instead of destroying it (which cancelled brief loads).
 */
export function manageBriefTabsMatcher(segments: UrlSegment[]): UrlMatchResult | null {
  if (segments.length < 1 || segments.length > 3) {
    return null;
  }
  const posParams: Record<string, UrlSegment> = {
    briefId: segments[0],
  };
  if (segments.length > 1) {
    posParams['tab'] = segments[1];
  }
  if (segments.length > 2) {
    posParams['subTab'] = segments[2];
  }
  return { consumed: segments, posParams };
}

export const MANAGE_BRIEF_TAB_SLUG_BY_TAB: Record<number, string> = {
  [BriefProgressEnum.ActivityFeed]: 'activity-feed',
  [BriefProgressEnum.Briefing]: 'briefing',
  [BriefProgressEnum.Assignment]: 'assignment',
  [BriefProgressEnum.ApprovedContent]: 'approved-content',
  [BriefProgressEnum.Logistics]: 'logistics',
  [BriefProgressEnum.Reporting]: 'reporting',
  [BriefProgressEnum.HybridReporting]: 'hybrid-reporting',
  [BriefProgressEnum.SparkAdsV3]: 'ads',
  [BriefProgressEnum.TikTokOneReporting]: 'performance',
  [BriefProgressEnum.Retro]: 'retro',
  [BriefProgressEnum.Moderation]: 'moderation',
  [BriefProgressEnum.BoostTikTok]: 'boost-tiktok',
  [BriefProgressEnum.BoostMeta]: 'boost-meta',
  [BriefProgressEnum.Complete]: 'complete',
};

const TAB_BY_SLUG: Record<string, BriefProgressEnum> = Object.fromEntries(
  Object.entries(MANAGE_BRIEF_TAB_SLUG_BY_TAB).map(([tab, slug]) => [slug, Number(tab) as BriefProgressEnum]),
);

export function slugForManageBriefTab(tab: BriefProgressEnum): string {
  return MANAGE_BRIEF_TAB_SLUG_BY_TAB[tab] ?? MANAGE_BRIEF_TAB_SLUG_BY_TAB[DEFAULT_MANAGE_BRIEF_TAB];
}

export function tabFromManageBriefParam(param: string | null | undefined): BriefProgressEnum {
  if (!param) {
    return DEFAULT_MANAGE_BRIEF_TAB;
  }
  const fromSlug = TAB_BY_SLUG[param];
  if (fromSlug != null) {
    return fromSlug;
  }
  const asNumber = Number(param);
  if (Number.isInteger(asNumber) && MANAGE_BRIEF_TAB_SLUG_BY_TAB[asNumber]) {
    return asNumber as BriefProgressEnum;
  }
  return DEFAULT_MANAGE_BRIEF_TAB;
}

/** Assignment status filters are 1–2 digit numbers. Creator IDs are Firebase UIDs. */
export function isManageBriefAssignmentSubTab(segment: string | null | undefined): boolean {
  return !!segment && /^\d{1,2}$/.test(segment);
}

export function creatorIdFromManageBriefDetail(segment: string | null | undefined): string | undefined {
  if (!segment || isManageBriefAssignmentSubTab(segment)) {
    return undefined;
  }
  return segment;
}

export function manageBriefCommands(
  briefId: string,
  tab: BriefProgressEnum,
  extra?: { subTab?: number; creatorId?: string | null },
): (string | number)[] {
  const commands: (string | number)[] = ['/manage-brief', briefId, slugForManageBriefTab(tab)];
  if (extra?.creatorId) {
    commands.push(extra.creatorId);
  } else if (extra?.subTab != null && extra.subTab > 0) {
    commands.push(extra.subTab);
  }
  return commands;
}
